import { createSlice } from "@reduxjs/toolkit";

import {
  ChartActivityInfo,
  DashboardLocIdInfo,
  GeoLocaDashboard,
} from "../../actions/dashboard/dashboardTypes";
import { getPocketLabRecentVersion } from "../../actions/dashboard/dashboardAsyncActions";
import { logOut } from "../layout";
import {
  fetchCerealSlugs,
  fetchCropInfo,
} from "../../actions/dashboard/dynamicCharts";
import { activityChartValues } from "../../actions/dashboard/dashboardDataAsync.actions";

export interface CerealSlugs {
  slug: string;
  translation: string;
  id: number;
}

export interface DashboardState {
  loadingDashboardInfo: boolean;
  dateRangePickerDashboard: boolean;
  isComingFromMap: boolean;
  geoLocInfo: GeoLocaDashboard;
  error: string;
  geoLocNameSelected: string;
  chartActivity: ChartActivityInfo[];

  allLocations: boolean;
  locationsSelected: DashboardLocIdInfo[];
  pocketlabVersion: string;

  pocketLabChart: any[];
  rawRowsActivityTable: any[];
  countOfUsers: {
    countOfUsersActive: number;
    countOfUsersInactive: number;
    totalUsers: number;
  };
  rawRowsPocketLabTable: any[];
  comesFromMap: boolean;
  cropsSelected: CerealSlugs[];

  /// new dynamic charts state
  loading: {
    slugs: boolean;
    dynamicCharts: boolean;
  };
  errors: {
    slugs: string;
    dynamicCharts: string;
  };
  selectedCrop: {
    slug: string;
    translation: string;
  };

  dynamicCharts: {
    createArrayForCharts: { variety: string; actions: number }[];
    sumActions: number;
    CreateArrayAverageForAvgChart: { variety: string; average: number }[];
  };

  activityChart: {
    data: any[];
    loading: boolean;
    error: string;
  };

  allDataResults: {
    data: Array<{
      specie: string;
      countInfo: { date: string; activity: string }[];
    }>;
  };

  usersTable: Array<{
    user: string;
    site: string;
    totalActions: number;
    varietyActions: Array<{
      cereal: string;
      count: number;
    }>;
  }>;

  hasDataInCharts: {
    hasData: boolean;
    hasDataBeenLoaded: boolean;
  };
}

export const initialState: DashboardState = {
  loadingDashboardInfo: false,
  dateRangePickerDashboard: false,
  pocketlabVersion: "",
  isComingFromMap: false,
  geoLocInfo: {
    data: {
      total: 0,
      data: [{ id: 0, activity: {} }],
    },
  } as GeoLocaDashboard,
  error: "",
  geoLocNameSelected: "",
  chartActivity: [],

  allLocations: false,
  locationsSelected: [] as DashboardLocIdInfo[],

  pocketLabChart: [["0.0.0", 1]],
  rawRowsActivityTable: [],
  rawRowsPocketLabTable: [],
  countOfUsers: {
    countOfUsersActive: 0,
    countOfUsersInactive: 0,
    totalUsers: 0,
  },

  comesFromMap: false,
  cropsSelected: [] as CerealSlugs[],
  loading: {
    slugs: false,
    dynamicCharts: false,
  },
  errors: {
    slugs: "",
    dynamicCharts: "",
  },
  selectedCrop: {
    slug: "",
    translation: "",
  },

  dynamicCharts: {
    createArrayForCharts: [{ variety: "", actions: 0 }],
    sumActions: 0,
    CreateArrayAverageForAvgChart: [{ variety: "", average: 0 }],
  },

  activityChart: {
    data: [],
    loading: false,
    error: "",
  },

  allDataResults: {
    data: [
      {
        specie: "",
        countInfo: [{ date: "", activity: "" }],
      },
    ],
  },
  usersTable: [
    {
      user: "",
      site: "",
      totalActions: 0,
      varietyActions: [
        {
          cereal: "",
          count: 0,
        },
      ],
    },
  ],

  hasDataInCharts: {
    hasData: false,
    hasDataBeenLoaded: false,
  },
};

export const DashboardSlice = createSlice({
  name: "DashboardReducer",
  initialState,
  reducers: {
    setHasDataInCharts(state, action) {
      state.hasDataInCharts = action.payload;
    },

    resetAll(state) {
      return {
        ...state,
        usersTable: initialState.usersTable,
        allDataResults: initialState.allDataResults,
        chartActivity: initialState.chartActivity,
        countOfUsers: initialState.countOfUsers,
        pocketLabChart: initialState.pocketLabChart,
        rawRowsPocketLabTable: initialState.rawRowsPocketLabTable,
        dynamicCharts: initialState.dynamicCharts,
        selectedCrop: initialState.selectedCrop,
        comesFromMap: initialState.comesFromMap,
      };
    },
    setUsersTable(state, action) {
      return {
        ...state,
        usersTable: action.payload,
      };
    },

    setCrop(state, action) {
      return {
        ...state,
        selectedCrop: action.payload,
      };
    },
    setComesFromMap(state, action) {
      return {
        ...state,
        comesFromMap: action.payload,
      };
    },
    setPocketLabChartData(state, action) {
      return {
        ...state,
        pocketLabChart: action.payload,
      };
    },
    setRowsPocketlabTable(state, action) {
      return {
        ...state,
        rawRowsPocketLabTable: action.payload,
      };
    },

    setVisibleRangeDashboardPicker(state, action) {
      return {
        ...state,
        dateRangePickerDashboard: action.payload,
      };
    },

    /**
     * @Charts
     */
    setCounOfUserActivityTab(state, action) {
      return {
        ...state,
        countOfUsers: action.payload,
      };
    },

    setLocationNameDashboardInput(state, action) {
      return {
        ...state,
        geoLocNameSelected: action.payload,
      };
    },

    setAllSilos(state, action) {
      return {
        ...state,
        allLocations: action.payload,
      };
    },

    resetChartAcivity(state) {
      return {
        ...state,
        chartActivity: initialState.chartActivity,
        countOfUsers: initialState.countOfUsers,
        pocketLabChart: initialState.pocketLabChart,
      };
    },

    logoutDashboard() {
      return initialState;
    },

    /**
     * @Charts
     */
  },

  extraReducers(builder) {
    builder
      .addCase(fetchCropInfo.pending, (state) => {
        state.loading.dynamicCharts = true;
        state.dynamicCharts = initialState.dynamicCharts;
        state.errors.dynamicCharts = "";
      })
      .addCase(fetchCropInfo.fulfilled, (state, action) => {
        state.loading.dynamicCharts = false;
        state.dynamicCharts = action.payload as DashboardState["dynamicCharts"];
      })
      .addCase(fetchCropInfo.rejected, (state, action) => {
        state.loading.dynamicCharts = false;
        state.errors.dynamicCharts = action.payload as string;
      });

    builder
      .addCase(fetchCerealSlugs.pending, (state) => {
        state.loading.slugs = true;
        state.errors.slugs = "";
      })
      .addCase(fetchCerealSlugs.fulfilled, (state, action) => {
        state.loading.slugs = false;
        state.cropsSelected = action.payload;
      })
      .addCase(fetchCerealSlugs.rejected, (state, action) => {
        state.loading.slugs = false;
        state.errors.slugs = action.payload as string;
      });

    builder
      .addCase(getPocketLabRecentVersion.pending, (state) => {
        state.loadingDashboardInfo = true;
        state.error = "";
      })
      .addCase(getPocketLabRecentVersion.fulfilled, (state, action) => {
        state.loadingDashboardInfo = false;
        state.pocketlabVersion = action.payload;
      })
      .addCase(getPocketLabRecentVersion.rejected, (state, action) => {
        state.loadingDashboardInfo = false;
        state.error = action.payload as string;
      });

    builder.addCase(activityChartValues.pending, (state) => {
      state.activityChart.loading = true;
      state.activityChart.error = "";
    });
    builder.addCase(activityChartValues.fulfilled, (state, action) => {
      state.activityChart.loading = false;
      state.allDataResults = action.payload as { data: [] };
    });
    builder.addCase(activityChartValues.rejected, (state, action) => {
      state.activityChart.loading = false;
      state.allDataResults = initialState.allDataResults;
      state.activityChart.error = action.payload as string;
    });

    builder.addCase(logOut, () => {
      return initialState;
    });
  },
});
export const {
  setHasDataInCharts,
  resetAll,
  setCounOfUserActivityTab,
  setComesFromMap,
  setPocketLabChartData,
  setRowsPocketlabTable,
  setVisibleRangeDashboardPicker,
  setLocationNameDashboardInput,
  resetChartAcivity,

  setAllSilos,
  logoutDashboard,
  setCrop,
  setUsersTable,
} = DashboardSlice.actions;
export const Dashboard = DashboardSlice.reducer;
